import { type CalendarDate, fromAbsolute, getLocalTimeZone, type ZonedDateTime } from '@internationalized/date';
import {
  Combobox,
  DatePicker,
  InlineStack,
  Select,
  Text,
  TextField,
  Tooltip,
} from '@customerio/pluma-components/react';
import plur from 'plur';
import {
  ONE_DAY_FROM_NOW_SECONDS,
  TIME_DIRECTION_AGO,
  TIME_DIRECTION_FROM_NOW,
  attributeConditionOperatorGroupsForField,
  isAbsoluteTimestampOperator,
  isRelativeTimestampOperator,
  isTimestampInequalityOperator,
  isTwoValueFilterOperator,
  isUnaryFilterOperator,
  normalizeTimestampOperatorForSelect,
  relativeTimestampDays,
  relativeTimestampDirection,
  relativeTimestampForTypedDays,
  relativeTimestampWithDirection,
  timestampOperatorForVariant,
  type AttributeFilterState,
} from './dryad-utils';

type OperatorValuePatch = Partial<Pick<AttributeFilterState, 'operator' | 'value' | 'value2'>>;

type ValueOption = { value: string; label: string };

type OperatorValueProps = {
  disabled?: boolean;
  field: string;
  isLoadingValueOptions?: boolean;
  operator: string;
  value: string | null;
  valueOptions: ValueOption[];
  value2: string | null;
  onChange: (patch: OperatorValuePatch) => void;
};

type TimestampVariant = 'relative' | 'absolute';
type DatePickerValue = CalendarDate | ZonedDateTime | { start: CalendarDate; end: CalendarDate } | null;

function clearsValues(nextOperator: string, prevOperator: string): boolean {
  if (isUnaryFilterOperator(nextOperator)) {
    return true;
  }
  return nextOperator === 'timestamp_bw' || prevOperator === 'timestamp_bw';
}

// A relative offset is seconds; every other operator's value is free text. Moving
// between the two carries nothing across, and arriving at a relative operator
// starts where the legacy row did — one day from now.
function valueForOperatorChange(nextOperator: string, prevOperator: string, value: string | null): string | null {
  // Checked before clearsValues, which would otherwise strip the seed on the one
  // transition that comes from 'between' and leave that row alone in landing
  // invalid.
  if (isRelativeTimestampOperator(nextOperator)) {
    return isRelativeTimestampOperator(prevOperator) ? value : ONE_DAY_FROM_NOW_SECONDS;
  }
  if (clearsValues(nextOperator, prevOperator)) {
    return null;
  }
  return isRelativeTimestampOperator(prevOperator) ? null : value;
}

function epochToDate(value: string | null): ZonedDateTime | null {
  if (value == null || value.trim() === '') {
    return null;
  }
  const seconds = Number.parseInt(value, 10);
  return Number.isNaN(seconds) ? null : fromAbsolute(seconds * 1000, getLocalTimeZone());
}

function dateToEpoch(value: DatePickerValue): string | null {
  if (value == null || 'start' in value) {
    return null;
  }
  const date = 'timeZone' in value ? value.toDate() : value.toDate(getLocalTimeZone());
  return String(Math.floor(date.getTime() / 1000));
}

function timestampVariant(operator: string): TimestampVariant {
  return isAbsoluteTimestampOperator(operator) ? 'absolute' : 'relative';
}

// Computed from the stored seconds, and with a time: the day field rounds, so
// this is the only place a reader can see an offset finer than a day.
function relativeTimestampOutput(value: string | null): string {
  return new Date(Date.now() + Number(value ?? '0') * 1000).toLocaleString();
}

type OperatorSelectProps = Pick<
  OperatorValueProps,
  'disabled' | 'field' | 'operator' | 'value' | 'value2' | 'onChange'
>;

function OperatorSelect({ disabled, field, operator, value, value2, onChange }: OperatorSelectProps) {
  const selectedTimestampVariant = timestampVariant(operator);
  const handleOperatorChange = (nextOperator: string | null) => {
    if (nextOperator == null) return;
    const transformedOperator = timestampOperatorForVariant(nextOperator, selectedTimestampVariant);
    const clearAll = clearsValues(transformedOperator, operator);
    onChange({
      operator: transformedOperator,
      value: valueForOperatorChange(transformedOperator, operator, value),
      value2: clearAll || !isTwoValueFilterOperator(transformedOperator) ? null : value2,
    });
  };

  return (
    <Select
      ariaLabel="Operator"
      size="sm"
      options={attributeConditionOperatorGroupsForField(field)}
      value={normalizeTimestampOperatorForSelect(operator)}
      onChange={handleOperatorChange}
      isDisabled={disabled}
    />
  );
}

type TimestampVariantSelectProps = Pick<OperatorValueProps, 'disabled' | 'operator' | 'value' | 'onChange'>;

function TimestampVariantSelect({ disabled, operator, value, onChange }: TimestampVariantSelectProps) {
  const selectedTimestampVariant = timestampVariant(operator);
  const handleTimestampVariantChange = (variant: string | null) => {
    if (variant !== 'relative' && variant !== 'absolute') return;
    const nextOperator = timestampOperatorForVariant(operator, variant);
    onChange({ operator: nextOperator, value: valueForOperatorChange(nextOperator, operator, value) });
  };

  return (
    <>
      <Select
        ariaLabel="Timestamp variant"
        size="sm"
        options={[
          { value: 'relative', label: 'A relative date' },
          { value: 'absolute', label: 'A specific date' },
        ]}
        value={selectedTimestampVariant}
        onChange={handleTimestampVariantChange}
        isDisabled={disabled}
      />
      <Text as="span" color="subtle">
        of
      </Text>
    </>
  );
}

type RelativeTimestampValueProps = Pick<OperatorValueProps, 'disabled' | 'field' | 'operator' | 'value' | 'onChange'>;

function RelativeTimestampValue({ disabled, field, operator, value, onChange }: RelativeTimestampValueProps) {
  const days = relativeTimestampDays(value);
  const direction = relativeTimestampDirection(value);

  return (
    <>
      <TextField
        ariaLabel="Timestamp"
        type="number"
        min="0"
        size="sm"
        placeholder="1"
        value={String(days)}
        style={{ width: '75px' }}
        onInput={(event) => {
          if (event.target instanceof HTMLInputElement) {
            onChange({ value: relativeTimestampForTypedDays(event.target.value, value) });
          }
        }}
        isDisabled={disabled}
      />
      <Text as="span" color="subtle">
        {plur('day', days)}
      </Text>
      <Select
        ariaLabel="Time direction"
        size="sm"
        options={[
          { value: TIME_DIRECTION_AGO, label: 'Ago' },
          { value: TIME_DIRECTION_FROM_NOW, label: 'From now' },
        ]}
        value={direction}
        onChange={(nextDirection) => {
          if (nextDirection != null) {
            onChange({ value: relativeTimestampWithDirection(value, nextDirection) });
          }
        }}
        isDisabled={disabled === true || Number(value ?? '0') === 0}
      />
      <Tooltip
        placement="bottom"
        content={`If run right now, this condition would find items with ${field} date on or ${
          operator === 'timestamp_lt' ? 'before' : 'after'
        } ${relativeTimestampOutput(value)}.`}
      >
        <Text as="span" color="subtle" style={{ textDecoration: 'underline dotted' }}>
          What's the output?
        </Text>
      </Tooltip>
    </>
  );
}

type AbsoluteTimestampValueProps = Pick<OperatorValueProps, 'disabled' | 'value' | 'onChange'>;

function AbsoluteTimestampValue({ disabled, value, onChange }: AbsoluteTimestampValueProps) {
  return (
    <DatePicker
      ariaLabel="Date and time"
      withTime={true}
      placeholder="Date and time"
      size="sm"
      value={epochToDate(value)}
      onChange={(nextDate) => onChange({ value: dateToEpoch(nextDate) })}
      isDisabled={disabled}
    />
  );
}

type TimestampRangeValueProps = Pick<OperatorValueProps, 'disabled' | 'value' | 'value2' | 'onChange'>;

function TimestampRangeValue({ disabled, value, value2, onChange }: TimestampRangeValueProps) {
  return (
    <>
      <DatePicker
        ariaLabel="Start date and time"
        withTime={true}
        placeholder="Date and time"
        size="sm"
        value={epochToDate(value)}
        onChange={(nextDate) => onChange({ value: dateToEpoch(nextDate) })}
        isDisabled={disabled}
      />
      <Text as="span" color="subtle">
        and
      </Text>
      <DatePicker
        ariaLabel="End date and time"
        withTime={true}
        placeholder="Date and time"
        size="sm"
        value={epochToDate(value2)}
        onChange={(nextDate) => onChange({ value2: dateToEpoch(nextDate) })}
        isDisabled={disabled}
      />
    </>
  );
}

type ValueComboboxProps = {
  ariaLabel: string;
  disabled?: boolean;
  inputValue: string | null;
  isLoadingValueOptions?: boolean;
  placeholder?: string;
  valueOptions: ValueOption[];
  onValueChange: (value: string) => void;
};

function ValueCombobox({
  ariaLabel,
  disabled,
  inputValue,
  isLoadingValueOptions,
  placeholder = 'Value',
  valueOptions,
  onValueChange,
}: ValueComboboxProps) {
  return (
    <Combobox
      ariaLabel={ariaLabel}
      className="fs-mask"
      size="sm"
      placeholder={placeholder}
      inputValue={inputValue ?? ''}
      onValueChange={onValueChange}
      options={valueOptions}
      isLoading={isLoadingValueOptions}
      isDisabled={disabled}
      flexGrow="1"
      withVirtualizer
    />
  );
}

type TwoValueInputsProps = Pick<
  OperatorValueProps,
  'disabled' | 'isLoadingValueOptions' | 'value' | 'value2' | 'valueOptions' | 'onChange'
>;

function TwoValueInputs({
  disabled,
  isLoadingValueOptions,
  value,
  value2,
  valueOptions,
  onChange,
}: TwoValueInputsProps) {
  return (
    <>
      <ValueCombobox
        ariaLabel="Value"
        disabled={disabled}
        inputValue={value}
        isLoadingValueOptions={isLoadingValueOptions}
        placeholder="Value"
        valueOptions={valueOptions}
        onValueChange={(nextValue) => onChange({ value: nextValue })}
      />
      <Text as="span" color="subtle">
        and
      </Text>
      <ValueCombobox
        ariaLabel="Second value"
        disabled={disabled}
        inputValue={value2}
        isLoadingValueOptions={isLoadingValueOptions}
        valueOptions={valueOptions}
        onValueChange={(nextValue) => onChange({ value2: nextValue })}
      />
    </>
  );
}

export function OperatorValue(props: OperatorValueProps) {
  const { disabled, isLoadingValueOptions, operator, value, valueOptions, value2, onChange } = props;
  const showTimestampInequality = isTimestampInequalityOperator(operator);
  const showRelativeTimestamp = isRelativeTimestampOperator(operator);
  const showAbsoluteTimestamp = isAbsoluteTimestampOperator(operator);
  const showTimestampRange = operator === 'timestamp_bw';
  const showSingleValue =
    !showTimestampInequality && !isUnaryFilterOperator(operator) && !isTwoValueFilterOperator(operator);
  const showTwoValues = isTwoValueFilterOperator(operator) && !showTimestampRange;

  return (
    <InlineStack alignBlock="center" gap="050" wrap>
      <OperatorSelect {...props} />
      {showTimestampInequality ? <TimestampVariantSelect {...props} /> : null}
      {showRelativeTimestamp ? <RelativeTimestampValue {...props} /> : null}
      {showAbsoluteTimestamp ? <AbsoluteTimestampValue disabled={disabled} value={value} onChange={onChange} /> : null}
      {showTimestampRange ? (
        <TimestampRangeValue disabled={disabled} value={value} value2={value2} onChange={onChange} />
      ) : null}
      {showTwoValues ? (
        <TwoValueInputs
          disabled={disabled}
          isLoadingValueOptions={isLoadingValueOptions}
          value={value}
          value2={value2}
          valueOptions={valueOptions}
          onChange={onChange}
        />
      ) : null}
      {showSingleValue ? (
        <ValueCombobox
          ariaLabel="Value"
          disabled={disabled}
          inputValue={value}
          isLoadingValueOptions={isLoadingValueOptions}
          valueOptions={valueOptions}
          onValueChange={(nextValue) => onChange({ value: nextValue })}
        />
      ) : null}
    </InlineStack>
  );
}
